import { VEC2_ZERO, type Vec2, distance, subtract } from '@d2/core';

/**
 * One pointer press, and the question it has to answer: was that a click or a
 * drag?
 *
 * Both gestures start identically — the left button goes down over the cave —
 * and they mean opposite things: a click orders a walk, a drag pans the camera
 * (TODO row 62). Nothing can tell them apart at the moment of the press, so the
 * answer is deferred until the pointer has either moved far enough to be a drag
 * or been released without doing so.
 *
 * `DRAG_THRESHOLD_PX` is the whole of the resolution. Below it the pointer is
 * treated as still, which is what makes a click survive the pixel or two a hand
 * moves while pressing a button — the reason every desktop toolkit has this
 * constant. Above it the gesture is a drag *and stays one*, even if the pointer
 * comes back: dragging the world somewhere and returning it is a cancelled pan,
 * not an order to walk.
 *
 * Kept here rather than in the scene for the usual reason: a rule inside a
 * Phaser scene is a rule nothing can unit-test. It is pure and has no notion of
 * a camera, a canvas or a button — the scene feeds it pointer positions and
 * spends the pan it hands back. The general input abstraction (TODO row 2) will
 * consume this, not replace it.
 */

/**
 * How far the pointer may travel and still be a click, in canvas pixels.
 *
 * Six. Windows uses four, browsers commonly five, and touch — desktop first,
 * touch-ready (`docs/GAME-DESIGN.md`) — wants more than either, because a finger
 * rolls further than a mouse does while pressing. Six is the smallest number
 * that absorbs a mouse wobble with room to spare while still being far below
 * the distance a deliberate drag covers in its first frame, so no drag is ever
 * mistaken for a click.
 */
export const DRAG_THRESHOLD_PX = 6;

/** A press in progress: where it began, where the pointer is, and what it has become. */
export interface Gesture {
  /** Where the button went down. The threshold is measured from here, never from the last frame. */
  readonly origin: Vec2;
  /** Where the pointer was last seen. */
  readonly last: Vec2;
  /** `true` once the threshold has been crossed. One-way: a drag never turns back into a click. */
  readonly dragging: boolean;
}

/** A gesture one move older, and the camera movement that move is worth. */
export interface GestureMove {
  readonly gesture: Gesture;
  /** Canvas pixels the world should follow. Zero while the gesture is still a click. */
  readonly pan: Vec2;
}

/** The press itself. A gesture begins as a click and only a move can change that. */
export const beginGesture = (point: Vec2): Gesture => ({ origin: point, last: point, dragging: false });

/**
 * The pointer moved. Hands back the gesture it has become and the ground the
 * camera should cover.
 *
 * The pan is measured from the *origin* on the frame the threshold is crossed
 * and from the previous point on every frame after it. That is what keeps the
 * world pinned to the grabbed point: the six pixels spent deciding this was a
 * drag are paid back in full on the frame the decision is made, so a drag pans
 * exactly as far as the pointer travelled and the world never slides under the
 * cursor by the width of the threshold.
 */
export const moveGesture = (gesture: Gesture, point: Vec2, threshold = DRAG_THRESHOLD_PX): GestureMove => {
  if (gesture.dragging) {
    return { gesture: { ...gesture, last: point }, pan: subtract(point, gesture.last) };
  }

  const travelled = subtract(point, gesture.origin);

  if (distance(gesture.origin, point) < threshold) {
    return { gesture: { ...gesture, last: point }, pan: VEC2_ZERO };
  }

  return { gesture: { ...gesture, last: point, dragging: true }, pan: travelled };
};

/**
 * `true` when releasing now means "walk here".
 *
 * The negative case is the important one: a released drag must order nothing,
 * or every pan would end in the entity setting off across the cave.
 */
export const isClick = (gesture: Gesture): boolean => !gesture.dragging;
